import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subscription } from "rxjs";
import { ContactsService, PersonContactsService, ReservationContactsService } from "@services/dws";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { toast } from "projects/web/src/util/toast";
import { tr } from "@util/tr";
import { observe } from "projects/web/src/util/loading/loading";
import { confirm } from "../dialogs/confirm";
import {
  CountryComplete,
  CountriesCompleteService,
} from "@services/countries-complete.service";
import { locale } from "@util/locale";
import { Reservation } from "@interfaces/dws/reservation";
import { ValueInListValidator } from "@services/validators/value-in-list-validator";
import { StorageService } from "@services/storage.service";
import { Contact } from "@interfaces/dws/contact";

export interface CountryPrefix {
  id: string;
  isoCode2: string[];
  isoCode3: string[];
  phonePrefix: string;
}

@Component({
  selector: "app-new-contact-sidenav",
  templateUrl: "./new-contact-sidenav.component.html",
  styleUrls: ["./new-contact-sidenav.component.scss"],
})
export class NewContactSidenavComponent implements OnInit, OnDestroy {
  @Output() close: EventEmitter<Reservation.ReservationContactDTO> =
    new EventEmitter<Reservation.ReservationContactDTO>();
  @Input() wineryId!: string;
  @Input() isAppointment = false;
  @Input() reservationId?: string;
  private _isTourOperator = false;
  @Input()
  set isTourOperator(value: boolean) {
    this._isTourOperator = !!value;
    this._recomputeEffectiveTourOperator();
  }
  get isTourOperator(): boolean {
    return this._isTourOperator;
  }
  effectiveTourOperator = false;
  @Input() set contact(contact: Reservation.ReservationContactDTO) {
    this._contactTypologySub?.unsubscribe();
    this._contactTypologySub = undefined;
    this._crmSuggestsTourOperator = false;

    this.form.reset();
    this.crmContactId = contact?.crmContactId;
    this._editingContactIsSecondary = contact?.contactType === "SECONDARY";
    this.newContact = !this.crmContactId;
    this.contactIsNew = !contact?.id || (typeof (contact as any).id == 'string' && (contact as any).id.includes('-'));
    if (contact) {
      const prefixVal = contact.phoneNumber?.split(" ")[0].replace("+", "");
      this.form.patchValue({
        ...contact,
        mainPhonePrefix: prefixVal,
        phoneNumber: contact.phoneNumber?.split(" ")[1],
      });
      this._pendingPrefixVal = prefixVal;
      this.selectedPrefix = this.countriesPrefixData.find(x => x.phonePrefix === prefixVal) ?? null;
    }
    this._recomputeEffectiveTourOperator();
    this._loadContactTypology();
  }
  crmContactId: any;
  newContact!: boolean;
  private _contactTypologySub?: Subscription;
  private _crmSuggestsTourOperator = false;
  private _editingContactIsSecondary = false;

  form: FormGroup;
  countriesPrefixData: CountryPrefix[] = [];
  filteredCountriesPrefixData: CountryPrefix[] = [];
  selectedPrefix: CountryPrefix | null = null;
  private _pendingPrefixVal: string | undefined;
  prefixFormatter: (item: CountryPrefix) => string = (item) => this.displayPhonePrefix("", item);
  countriesIsoAndNames: {name: string, iso: string}[] = [];
  filteredCountriesIsoAndNames: {name: string, iso: string}[] = [];
  isSaving = false;
  contactEdit: Contact = {};
  contactIsNew: boolean = true;

  constructor(
    private fb: FormBuilder,
    private contactsService: ContactsService,
    private personContactsService: PersonContactsService,
    private countriesCompleteService: CountriesCompleteService,
    private reservationContactsService: ReservationContactsService,
    private storageService: StorageService
  ) {
    if(this.crmContactId){
      this.newContact = this.crmContactId == "new" || !this.crmContactId;
    }
    this.displayPhonePrefix = this.displayPhonePrefix.bind(this);
    this.displayCountry = this.displayCountry.bind(this);

    observe(this.countriesCompleteService.list()).subscribe((country) => {
      if (country) {
        this.getCountryPrefixData(country);
        this.getCountryIsoCodeData(country);
        // mainPhonePrefix conditional validator
        this.form.get("mainPhonePrefix")?.setValidators([
          (control: AbstractControl): ValidationErrors | null => {
            // Valid if empty
            if (!control.value || control.value.trim() === '') {
              return null;
            }
            // Value must be from the list
            const isValid = this.countriesPrefixData
              .map((x) => x.phonePrefix)
              .includes(control.value);
            return isValid ? null : { invalidPrefix: true };
          }
        ]);
        // countryIso conditional validator
        this.form.get("countryIso")?.setValidators([
          (control: AbstractControl): ValidationErrors | null => {
            // Valid if empty
            if (!control.value || control.value.trim() === '') {
              return null;
            }
            // Value must be from the list
            const isValid = this.countriesIsoAndNames
              .map((x) => x.iso)
              .includes(control.value);
            return isValid ? null : { invalidCountry: true };
          }
        ]);
        // Update validators
        this.form.get("mainPhonePrefix")?.updateValueAndValidity();
        this.form.get("countryIso")?.updateValueAndValidity();
      }
    });

    this.form = fb.group({
      id: [""],
      firstName: [""],
      lastName: [""],
      email: ["", [Validators.email]],
      phoneNumber: [""],
      countryIso: [""],
      contactType: ["MASTER"],
      mainPhonePrefix: [""],
      manualMarketing: [false],
    }, { validators: this.atLeastOneFieldValidator() });

    this.form.get("email")?.valueChanges.subscribe((value) => {
      if (!value?.length || this.form.get("email")?.invalid) {
        this.form.get("manualMarketing")?.setValue(false);
      }
    });
  }

  private atLeastOneFieldValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const form = formGroup as FormGroup;
      const firstName = form.get('firstName')?.value;
      const lastName = form.get('lastName')?.value;
      const phoneNumber = form.get('phoneNumber')?.value;
      const mainPhonePrefix = form.get('mainPhonePrefix')?.value;
      const email = form.get('email')?.value;
      const hasFirstName = firstName && firstName.trim().length > 0;
      const hasLastName = lastName && lastName.trim().length > 0;
      const hasPhone = phoneNumber && phoneNumber.trim().length > 0 && mainPhonePrefix && mainPhonePrefix.trim().length > 0;
      const hasEmail = email && email.trim().length > 0;
      if (hasFirstName || hasLastName || hasPhone || hasEmail) {
        return null; // Valid
      }
      return { atLeastOneRequired: true }; // Not valid
    };
  }

  private getCountryIsoCodeData(countries: CountryComplete[]) {
    let local: any = locale().locale;
    countries.forEach((country) => {
      this.countriesIsoAndNames.push({
        name:
          country.translations[local as keyof { en: string; it: string }] ||
          country.name,
        iso: country.alpha2Code,
      });
    });

    this.countriesIsoAndNames = this.sortCountries(this.countriesIsoAndNames);

    this.filteredCountriesIsoAndNames = this.countriesIsoAndNames;
  }

  private getCountryPrefixData(countries: CountryComplete[]) {
    countries.forEach((country) => {
      if (country.callingCodes) {
        country.callingCodes.forEach((prefixCode: string) => {
          const alreadyInListIndex = this.countriesPrefixData.findIndex(
            (countryPData) => countryPData.phonePrefix === prefixCode
          );
          if (alreadyInListIndex > 0) {
            this.countriesPrefixData[alreadyInListIndex].isoCode2.push(
              country.alpha2Code
            );
            this.countriesPrefixData[alreadyInListIndex].isoCode3.push(
              country.alpha3Code
            );
          } else {
            this.countriesPrefixData.push({
              id: prefixCode,
              phonePrefix: prefixCode,
              isoCode3: [country.alpha3Code],
              isoCode2: [country.alpha2Code],
            });
          }
        });
      }
    });

    this.form.get("mainPhonePrefix")?.valueChanges.subscribe((value) => {
      const mainPhonePrefix = value;
      const phone = this.form.get("phone")?.value || "";
      this.form.get("phone")?.setValue(`+${mainPhonePrefix} ${phone}`);
    });

    this.countriesPrefixData = this.sortPrefixes(this.countriesPrefixData);

    this.filteredCountriesPrefixData = this.countriesPrefixData;

    if (this._pendingPrefixVal) {
      this.selectedPrefix = this.countriesPrefixData.find(x => x.phonePrefix === this._pendingPrefixVal) ?? null;
    }
  }

  getPrefixWithPlus(): string {
    const prefix = this.form.get("mainPhonePrefix")?.value;
    const phone = this.form.get("phone")?.value;
    if (!prefix) {
      return "";
    }
    return `+${prefix} `;
  }

  ngOnInit() {
    if (this.isAppointment) {
      this.form.get("email")!.clearValidators();
    }
  }

  ngOnDestroy(): void {
    this._contactTypologySub?.unsubscribe();
  }

  private _recomputeEffectiveTourOperator(): void {
    if (this._editingContactIsSecondary) {
      this.effectiveTourOperator = false;
      return;
    }
    if (!this.crmContactId) {
      this.effectiveTourOperator = this.isTourOperator;
      return;
    }
    this.effectiveTourOperator = this._crmSuggestsTourOperator;
  }

  private _loadContactTypology(): void {
    if (!this.crmContactId || !this.wineryId) {
      return;
    }
    const requestId = this.crmContactId;
    this._contactTypologySub = this.contactsService.getv2(requestId, this.wineryId).subscribe((contact) => {
      if (requestId !== this.crmContactId) {
        return;
      }
      this._crmSuggestsTourOperator = !!(contact && (contact as any).typology !== "customerPrivate");
      this._recomputeEffectiveTourOperator();
    });
  }

  cancelClicked() {
    this.close.emit(undefined);
  }

  async saveClicked() {
    if (this.isSaving) {
      return;
    }
    try {
      // Prepare contact data from form values
      let toReturn = {...this.form.value, manualMarketing: Boolean(this.form.value.manualMarketing)};
      // Check if at least one contact information is provided
      if (
        !toReturn.firstName &&
        !toReturn.lastName &&
        !toReturn.email &&
        !toReturn.phoneNumber &&
        !toReturn.countryIso
      ) {
        toast(
          tr(
            "The reservation can't be saved without at least one client information"
          )
        );
        return;
      }
      // Validate email format if provided
      if (this.form.invalid) {
        toast(tr("Invalid email"));
        return;
      }
      // Warn user if contact won't be created in CRM (no email provided)
      if (!toReturn.email) {
        const cont = await confirm.yesno(
          tr(
            "The contact will be saved without email address, it won't be created in the CRM and transactional emails won't be sent. Do you want to continue?"
          )
        );
        if (!cont) {
          return;
        }
        // Don't create CRM contact if there's no email
        toReturn.crmContactId = null;
      } else {
        // Only set CRM contact ID if email is provided
        toReturn.crmContactId = this.crmContactId;
      }
      // Update storage with most used countries and phone prefixes for UI sorting
      this.updateTopCountriesAndPrefixes(
        {
          name: this.displayCountry(toReturn.countryIso),
          iso: toReturn.countryIso,
        },
        this.countriesPrefixData.find(
          (x) => x.phonePrefix === toReturn.mainPhonePrefix
        )!
      );
      // Format phone number with prefix and clean up unnecessary fields
      if (toReturn.phoneNumber) {
        toReturn.phoneNumber = this.getPrefixWithPlus() + toReturn.phoneNumber;
      }
      delete toReturn.mainPhonePrefix;
      toReturn.reservationId = this.reservationId;
      // Mark as tour operator if applicable
      if (this.effectiveTourOperator) {
        if (!toReturn.otherData) toReturn.otherData = {};
        toReturn.otherData.tourOperator = true;
        toReturn.lastName = "";
      }
      this.isSaving = true;
      // Create new contact or update existing one
      if (this.newContact) {
        delete toReturn.id;
        delete toReturn.reservationId;
        if (this.isAppointment) {
          this.close.emit(toReturn);
          this.isSaving = false;
          return;
        }
        this.personContactsService.create(
          {...toReturn}
        ).subscribe({
          next: () => {
            toast(tr("Contact created successfully"));
            console.log("Contact created successfully", toReturn);
            this.close.emit(toReturn);
            this.isSaving = false;
          },
          error: (e) => {
            console.error("Error creating contact", e);
            toast(tr("An error occurred. Try later or contact support team."));
            this.isSaving = false;
          }
        });
      } else {
        this.close.emit(toReturn);
        this.isSaving = false;
        this.contactsService.updateShort(this.crmContactId, {...toReturn}).toPromise()
          .then(() => toast(tr("Contact updated successfully")))
          .catch((e) => {
            console.error("Error updating contact", e);
            toast(tr("An error occurred. Try later or contact support team."));
          });
      }
    } catch (error) {
      // Handle any unexpected errors during the save process
      console.error("Error saving contact", error);
      toast(tr("An error occurred. Try later or contact support team."));
      this.isSaving = false;
    }
  }

  onPrefixSelected(prefix: CountryPrefix) {
    const prev = this.form.get("mainPhonePrefix")?.value;
    if (prefix.phonePrefix === prev) {
      this.selectedPrefix = prefix;
      return;
    }
    const phoneCtrl = this.form.get("phoneNumber");
    const nationalDigits = String(phoneCtrl?.value ?? "").trim();
    this.selectedPrefix = prefix;
    this.form.get("mainPhonePrefix")?.setValue(prefix.phonePrefix);
    queueMicrotask(() => {
      phoneCtrl?.setValue(nationalDigits, { emitEvent: true });
    });
  }

  filterCountries(event: Event) {
    let searchText = (event.target as HTMLInputElement).value || "";
    searchText = searchText.toLowerCase();

    this.filteredCountriesIsoAndNames = this.countriesIsoAndNames.filter(
      (country) =>
        country.iso.toLowerCase().includes(searchText) ||
        country.name.toLowerCase().includes(searchText)
    );
  }

  displayPhonePrefix(prefix: string, o?: CountryPrefix): string {
    var obj =
      o ||
      this.countriesPrefixData?.find(
        (countryPrefix) => countryPrefix.phonePrefix === prefix
      );
    return obj
      ? `+${obj.phonePrefix} | ${
          obj.isoCode3.find((x) => x == "USA") ? "USA" : obj.isoCode3[0]
        }`
      : "";}

  displayCountry(iso: string, o?: { name: string; iso: string }): string {
    var obj =
      o || this.countriesIsoAndNames?.find((country) => country.iso === iso);
    return obj ? obj.name : "";
  }

  updateTopCountriesAndPrefixes(
    country: { name: string; iso: string },
    prefix: CountryPrefix
  ) {
    let topCountries = this.storageService.load("topCountries");
    let topPrefixes = this.storageService.load("topPhonePrefixes");
    if (!topCountries) {
      topCountries = this.countriesIsoAndNames.map((x) => ({ ...x, count: 0 }));
    }
    if (!topPrefixes) {
      topPrefixes = this.countriesPrefixData.map((x) => ({ ...x, count: 0 }));
    }
    let countryIndex = topCountries.findIndex(
      (x: any) => x.iso === country.iso
    );
    let prefixIndex = topPrefixes.findIndex(
      (x: any) => x.phonePrefix === prefix?.phonePrefix
    );
    if (countryIndex >= 0) {
      topCountries[countryIndex].count++;
    }
    if (prefixIndex >= 0) {
      topPrefixes[prefixIndex].count++;
    }
    topCountries = topCountries.sort((a: any, b: any) => b.count - a.count);
    topPrefixes = topPrefixes.sort((a: any, b: any) => b.count - a.count);
    this.storageService.save("topCountries", topCountries);
    this.storageService.save("topPhonePrefixes", topPrefixes);
  }

  sortPrefixes(prefixes: CountryPrefix[]): CountryPrefix[] {
    let topPrefixes = this.storageService.load("topPhonePrefixes") || [];

    let italy = prefixes.find((prefix) => prefix.isoCode3.includes("ITA"));

    let topUsedPrefixes = topPrefixes
      .filter((topPrefix: any) => !topPrefix.isoCode3.includes("ITA"))
      .slice(0, 2)
      .map((topPrefix: any) => {
        return prefixes.find(
          (prefix) => prefix.phonePrefix === topPrefix.phonePrefix
        );
      })
      .filter(Boolean);

    let remainingPrefixes = prefixes
      .filter(
        (prefix) =>
          !prefix.isoCode3.includes("ITA") &&
          !topUsedPrefixes.some(
            (topPrefix: any) => topPrefix.phonePrefix === prefix.phonePrefix
          )
      )
      .sort((a, b) => a.phonePrefix.localeCompare(b.phonePrefix));

    return [
      ...(italy ? [italy] : []),
      ...topUsedPrefixes,
      ...remainingPrefixes,
    ];
  }

  sortCountries(
    countries: { name: string; iso: string }[]
  ): { name: string; iso: string }[] {
    let topCountries = this.storageService.load("topCountries") || [];

    let italy = countries.find((country) => country.iso === "IT");

    let topUsedCountries = topCountries
      .filter((topCountry: any) => topCountry.iso !== "IT")
      .slice(0, 2)
      .map((topCountry: any) => {
        return countries.find((country) => country.iso === topCountry.iso);
      })
      .filter(Boolean);

    let remainingCountries = countries
      .filter(
        (country) =>
          country.iso !== "IT" &&
          !topUsedCountries.some(
            (topCountry: any) => topCountry.iso === country.iso
          )
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return [
      ...(italy ? [italy] : []),
      ...topUsedCountries,
      ...remainingCountries,
    ];
  }
}